import React from 'react';
import { Property } from '../types';
import { formatCurrency, formatDate, formatNumber } from '../utils/formatters';

interface PropertyCardProps {
  property: Property;
  id?: string;
}

export const PropertyCard: React.FC<PropertyCardProps> = ({ property, id }) => {
  const isGrouped = property.is_grouped && property.items && property.items.length > 1;

  const proportion = property.proporcao_transmitida || 100;
  const isPartial = proportion < 99.9;
  
  const marketValue = (property.valor_transacao / (proportion / 100));
  
  const hasUsefulArea = property.area_util && property.area_util > 0;
  const relevantArea = hasUsefulArea 
    ? property.area_util!
    : (property.area_construida > 0 ? property.area_construida : property.area_terreno);
    
  const areaLabel = hasUsefulArea 
    ? 'Área Útil (Est.)' 
    : (property.area_construida > 0 ? 'Área Const.' : 'Área Terreno');

  const pricePerSqm = relevantArea > 0 ? marketValue / relevantArea : 0;

  return (
    <div id={id} className={`bg-white rounded-xl shadow-sm border ${isGrouped ? 'border-brand-200 ring-1 ring-brand-100' : 'border-gray-100'} hover:shadow-md transition-all duration-300 overflow-hidden group flex flex-col`}>
      <div className={`p-4 border-b ${isGrouped ? 'bg-brand-50/50 border-brand-100' : 'bg-gray-50/50 border-gray-50'} group-hover:bg-brand-50/30 transition-colors`}>
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 leading-snug" title={property.endereco}>
              {property.endereco}
            </h3>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
              {property.bairro && (
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {property.bairro}
                </span>
              )}
              {property.complemento && (
                <>
                  <span className="text-gray-300 hidden sm:inline">•</span>
                  <span className="text-sm font-medium text-brand-600 italic truncate">
                    {property.complemento}
                  </span>
                </>
              )}
            </div>
            
            {isGrouped && (
              <div className="flex items-center gap-2 mt-1.5">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-brand-600 text-white uppercase tracking-wider">
                  Transação Agrupada
                </span>
                <span className="text-xs text-brand-600 font-medium">
                  {property.items?.length} Matrículas Consolidadas
                </span>
              </div>
            )}
          </div>
          
          <div className="flex flex-col items-end gap-1">
             <div className={`text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${isPartial ? 'bg-amber-100 text-amber-700' : 'bg-brand-100 text-brand-700'}`}>
                {formatNumber(proportion)}% Transmitido
             </div>
             <p className="text-xs font-semibold text-gray-900">{formatDate(property.data_transacao)}</p>
          </div>
        </div>
      </div>

      <div className="p-4 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-end mb-1">
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                    {isPartial ? 'Valor Transação (Parcial)' : 'Valor da Transação'}
                </p>
                {pricePerSqm > 0 && (
                     <div className="flex flex-col items-end">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${hasUsefulArea ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-brand-50 text-brand-600'}`}>
                            {formatCurrency(pricePerSqm)}/m²
                        </span>
                        <span className="text-[9px] text-gray-400 font-medium lowercase">
                            base: {areaLabel}
                        </span>
                     </div>
                )}
              </div>
              <p className="text-2xl font-black text-gray-800">{formatCurrency(property.valor_transacao)}</p>
              
              {isPartial && (
                  <div className="mt-2 pl-3 border-l-2 border-amber-200">
                    <p className="text-[10px] text-amber-600 uppercase font-bold">Valor de Mercado (100%)</p>
                    <p className="text-sm font-bold text-amber-700">{formatCurrency(marketValue)}</p>
                  </div>
              )}
            </div>

            {property.valor_financiado && property.valor_financiado > 0 ? (
              <div className="p-3 bg-brand-50/50 rounded-lg border border-brand-100">
                <p className="text-[10px] text-brand-600 uppercase font-bold mb-1">Financiamento</p>
                <div className="flex justify-between items-center">
                  <p className="text-base font-bold text-brand-800">{formatCurrency(property.valor_financiado)}</p>
                  <p className="text-xs font-bold text-brand-600 bg-white px-2 py-0.5 rounded shadow-sm border border-brand-100">
                    {((property.valor_financiado / property.valor_transacao) * 100).toFixed(1)}% LTV
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-gray-400 text-xs font-medium italic">
                Sem financiamento registrado
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Área Const.</p>
                <p className="text-sm font-bold text-gray-700">
                  {formatNumber(property.area_construida)} <span className="text-gray-400 font-normal">m²</span>
                </p>
              </div>
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Área Terreno</p>
                <p className="text-sm font-bold text-gray-700">
                  {formatNumber(property.area_terreno)} <span className="text-gray-400 font-normal">m²</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {isGrouped ? (
              <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-slate-200/50 px-3 py-2 text-[10px] font-bold text-slate-600 uppercase">
                  Composição da Transação
                </div>
                <div className="divide-y divide-slate-100 max-h-[220px] overflow-y-auto">
                  {property.items?.map((item, idx) => (
                    <div key={idx} className="p-3 hover:bg-white transition-colors">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-sm font-bold text-slate-800 truncate">{item.complemento || 'Unidade'}</span>
                        <span className="text-xs font-bold text-brand-600">{formatCurrency(item.valor_transacao)}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                        <span className="flex items-center gap-1 text-slate-500">
                          <b className="text-slate-700">Matr:</b> {item.matricula || '-'}
                        </span>
                        {item.numero_contribuinte && (
                          <span className="flex items-center gap-1 text-amber-600">
                            <b className="text-amber-700 font-bold uppercase">SQL:</b> {item.numero_contribuinte}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
               <div className="space-y-4">
                 <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center p-2 bg-slate-50 rounded border border-slate-100">
                       <span className="text-[10px] font-bold text-slate-500 uppercase">Matrícula</span>
                       <span className="text-sm font-bold text-slate-800">{property.matricula || '-'}</span>
                    </div>
                    {property.numero_contribuinte && (
                      <div className="flex justify-between items-center p-2 bg-amber-50 rounded border border-amber-100 shadow-sm">
                         <span className="text-[10px] font-bold text-amber-600 uppercase">Contribuinte (SQL)</span>
                         <span className="text-sm font-black text-amber-700 tracking-tight">{property.numero_contribuinte}</span>
                      </div>
                    )}
                 </div>
               </div>
            )}

            <div className="mt-auto space-y-2">
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-100 truncate max-w-full">
                  {property.cartorio}
                </span>
                {property.tipo_financiamento && property.tipo_financiamento !== '-' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-green-50 text-green-700 border border-green-100">
                    {property.tipo_financiamento}
                  </span>
                )}
                {property.descricao_uso && property.descricao_uso !== '-' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase">
                    {property.descricao_uso}
                  </span>
                )}
              </div>
              {property.natureza_transacao && property.natureza_transacao !== '-' && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-100">
                  <svg className="w-3 h-3 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-[10px] font-bold uppercase tracking-tight truncate">{property.natureza_transacao}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};